import React, { useContext, useState } from 'react';
import Table from 'react-bootstrap/Table';
import DeleteButton from '../Generic/DeleteButton';
import {
  deleteManufacturer,
  getManufacturerDetailsLazy,
} from '../../clients/manufacturer_client';
import ManufacturerModal from './ManufacturerModal';
import ManufacturerDetailModal from './ManufacturerDetailModal';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import SortableHeaderCell from '../Generic/SortableHeaderCell';
import { logError } from '../../config_and_helpers/logger';

/**
 * Represents a table component to list manufacturers.
 * @param {Object} ManufacturerTable - The props for the ManufacturerTable component.
 * @param {Array} ManufacturerTable.manufacturerList - The list of manufacturers to be displayed in the table.
 * @param {Function} ManufacturerTable.clearSearchValue - The function to clear the search value.
 * @returns {JSX.Element} - The ManufacturerTable component.
 */
const ManufacturerTable = ({
  manufacturerList,
  clearSearchValue,
  handleDelete,
  sortBy = '',
  sortDirection = 'asc',
  onSort = null,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  // State to store manufacturers with full data for editing
  const [manufacturersWithFullData, setManufacturersWithFullData] = useState(
    {}
  );
  const [loadingManufacturers, setLoadingManufacturers] = useState({});

  // Function to fetch full manufacturer data for editing
  const fetchFullManufacturerData = async manufacturerId => {
    // Don't fetch if already loading or already fetched
    if (
      loadingManufacturers[manufacturerId] ||
      manufacturersWithFullData[manufacturerId]
    ) {
      return;
    }

    setLoadingManufacturers(prev => ({ ...prev, [manufacturerId]: true }));

    try {
      const fullManufacturerData = await getManufacturerDetailsLazy(
        token,
        manufacturerId
      );

      setManufacturersWithFullData(prev => ({
        ...prev,
        [manufacturerId]: fullManufacturerData,
      }));
    } catch (error) {
      logError('ManufacturerTable', 'fetchFullManufacturerData failed', error);
    } finally {
      setLoadingManufacturers(prev => ({ ...prev, [manufacturerId]: false }));
    }
  };

  // Function to get the best available manufacturer data (full data if available, otherwise list data)
  const getManufacturerDataForEditing = manufacturer => {
    return manufacturersWithFullData[manufacturer.id] || manufacturer;
  };

  return (
    // Table to list manufacturers
    <Table striped bordered hover>
      {/*Table header*/}
      <thead>
        <tr className="text-center">
          <th>#</th>
          <SortableHeaderCell
            label="Name"
            sortKey="name"
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <th>Website</th>
          {!isViewer ? <th>Actions</th> : null}
        </tr>
      </thead>
      {/*Table body*/}
      <tbody>
        {manufacturerList.map((manufacturer, index) => (
          // Table row for each manufacturer
          <tr key={manufacturer.id} className="text-center align-middle">
            {/* Display order number of manufacturer*/}
            <td>{index + 1}</td>
            {/* Display name of manufacturer*/}
            <td>
              {/* Clicking on the name opens the manufacturer detail modal*/}
              <ManufacturerDetailModal
                manufacturerObj={manufacturer}
                updateManufacturers={handleDelete}
                clearSearchValue={clearSearchValue}
              />
            </td>
            {/*Display manufacturer's website*/}
            <td>
              <a
                href={manufacturer.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                <NavigateToUrlIcon />
              </a>
            </td>
            {/*Display actions: Edit and Delete*/}
            {!isViewer && (
            <td className="d-flex flex-row align-items-center justify-content-evenly">
              <div>
                {/* Edit manufacturer information */}
                <ManufacturerModal
                  manufacturerObj={getManufacturerDataForEditing(manufacturer)}
                  clearSearchValue={clearSearchValue}
                  onEditButtonHover={() =>
                    fetchFullManufacturerData(manufacturer.id)
                  }
                  isEditButtonLoading={loadingManufacturers[manufacturer.id]}
                />
              </div>
              {/* Delete manufacturer */}
              <DeleteButton
                objectType="manufacturer"
                objectName={manufacturer.name}
                objectId={manufacturer.id}
                deleteFetchFunc={deleteManufacturer}
                onSuccessfulDelete={handleDelete}
                clearSearchValue={clearSearchValue}
              />
            </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};
export default ManufacturerTable;
